import { useTranslation } from "react-i18next";
import i18n from "i18next";
const t = (key: string, fallback?: string) => i18n.t(key, fallback || key);
import { DeleteOutlined, EditOutlined, EyeOutlined, HistoryOutlined, MoreOutlined, ShareAltOutlined, SwapOutlined } from "@ant-design/icons";
import { Avatar, Checkbox, Dropdown, Flex, Space, Tooltip } from "antd";
import { MenuProps } from "antd/lib";
import dayjs from "dayjs";
import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { routesConfig } from "../../../../../config/routes";
import { LearningMaterialV2Type } from "../../../../../types/learning-material";
import { getFirstLetter, RenderUnitIcon } from "../../../../../utils/functionUltils";
import "./ResourceCard.scss";
import { useLearningRepositoryStore } from "../../../../../stores/stores";

interface ResourceCardProps {
	record: any;
	onEdit?: (record: LearningMaterialV2Type) => void;
	onDelete?: (id: number, name: string) => void;
	onViewHistory?: (id: number) => void;
	onSharePermission?: (rec: LearningMaterialV2Type) => void;
	onMoveItem?: (id: number) => void;
	selectable?: boolean;
	selected?: boolean;
	onSelect?: () => void;
	disableActions?: boolean;
	modalMode?: string;
	showCheckbox?: boolean;
	isBeingMoved?: boolean;
}

const ResourceCard: FC<ResourceCardProps> = ({
	record,
	onEdit,
	onDelete,
	onViewHistory,
	selectable,
	selected,
	onSelect,
	disableActions,
	modalMode,
	onSharePermission,
	showCheckbox,
	onMoveItem,
	isBeingMoved
}) => {
  const { t } = useTranslation();
	const navigate = useNavigate();
	const { set } = useLearningRepositoryStore((s) => ({
		set: s.set
	}));

	const items: MenuProps["items"] = [];
	if (record?.isCanEdit) {
		items.push({
			key: "1",
			label: t("Chia sẻ", "Chia sẻ"),
			icon: <ShareAltOutlined className="table-view-icon" />,
			onClick: ({ domEvent }) => {
				domEvent.stopPropagation();
				onSharePermission?.(record);
			},
		});
	}

	if (record?.isCanEdit) {
		items.push({
			key: "2",
			label: t("Chỉnh sửa", "Chỉnh sửa"),
			icon: <EditOutlined className="table-view-icon" />,
			onClick: ({ domEvent }) => {
				domEvent.stopPropagation();
				onEdit?.(record);
			},
		});

		items.push({
			key: "3",
			label: t("Xoá", "Xoá"),
			icon: <DeleteOutlined className="table-view-icon" />,
			className: "text-danger-del",
			onClick: ({ domEvent }) => {
				domEvent.stopPropagation();
				onDelete?.(record?.id, record?.name);
			},
		});
	}

	if (record?.isUnit) {
		items.push({
			key: "4",
			label: t("Lịch sử sử dụng", "Lịch sử sử dụng"),
			icon: <HistoryOutlined className="table-view-icon" />,
			onClick: ({ domEvent }) => {
				domEvent.stopPropagation();
				onViewHistory?.(record?.id);
			},
		});
	}

	if (record?.isCanEdit) {
		items.push({
			key: "5",
			label: t("Di chuyển", "Di chuyển"),
			icon: <SwapOutlined className="table-view-icon" />,
			onClick: ({ domEvent }) => {
				domEvent.stopPropagation();
				onMoveItem?.(record?.id);
			},
		});
	}

	const handleCardClick = () => {
		if (isBeingMoved) {
			return;
		}

		const isUnit = record?.isUnit;
		const searchParams = new URLSearchParams(location.search);

		if (!isUnit) {
			if (record?.isMine) {
				searchParams.set("isMine", "true");
				searchParams.delete("isShared");
				set("tab", "1");
			} else {
				searchParams.set("isShared", "true");
				set("tab", "2");
				searchParams.delete("isMine");
			}
		}

		if ((selectable || modalMode === 'saveTemplate' || modalMode === 'moveItem') && !isUnit) {
			searchParams.set("folderId", record?.id);
			navigate({
				pathname: location.pathname,
				search: searchParams.toString(),
			});
			return;
		}

		if ((selectable || modalMode === 'saveTemplate' || modalMode === 'moveItem') && isUnit) {
			return;
		}

		const basePath = isUnit
			? `/course-library${routesConfig.learningMaterials}/detail/${record?.units?.[0]?.id}`
			: `/${routesConfig.courseLibrary}/${routesConfig.learningRepository}/${record?.id}${searchParams.toString() ? "?" + searchParams.toString() : ""}`;

		navigate(basePath);
	};

	const shouldShowCheckbox = () => {
		if (modalMode === 'saveTemplate' || modalMode === 'moveItem') return false;
		if (showCheckbox && record?.isCanEdit) return true;
		if (selectable && record?.isUnit) return true;
		return false;
	};

	const isDisabled = (modalMode === 'saveTemplate' || modalMode === 'moveItem') && (record?.isUnit || !record?.isCanEdit) || isBeingMoved
	// || modalMode === "moveItem" && record?.isChangedAuthor;
	const owner = record?.authors?.find((it: any) => it.permission === "1")

	return (
		<div
			className={`resource-card ${selected ? "selected" : ""} ${isDisabled ? "resource-card-disabled" : ""}`}
			onClick={isDisabled ? undefined : handleCardClick}
			style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
		>
			<div className="card-hover-overlay" />
			<div className="resource-card__header">
				<div className="flex gap-16 align-center">
					<div className="learning-icon">
						{record?.isUnit ? (
							<div>{RenderUnitIcon(record?.unitModule)}</div>
						) : record?.isMine ? (
							<img
								src="/assets/icons/folder-icon.svg"
								style={{ minWidth: 32, minHeight: 32 }}
							/>
						) : (
							<img
								src="/assets/icons/folder-shared.svg"
								style={{ minWidth: 32, minHeight: 32 }}
							/>
						)}
						{record?.isChangedAuthor && (
							<div className="learning-icon-child">
								<img src="/assets/icons/transfer-icon.svg" />
							</div>
						)}
					</div>

					<Tooltip placement="top" title={record?.name}>
						<div className="learning-material-name">
							{record?.name}
							{isBeingMoved && <span style={{ color: '#999', marginLeft: '8px' }}>({t('Đang di chuyển', 'Đang di chuyển')})</span>}
						</div>
					</Tooltip>
				</div>
				<div className="card-action">
					{shouldShowCheckbox() && (
						<Checkbox
							checked={selected}
							className="card-select"
							onClick={(e) => {
								e.stopPropagation();
								onSelect?.();
							}}
						/>
					)}
					<div className="card-menu">
						<Space>
							{items?.length === 0 ? (
								<EyeOutlined style={{ color: "#757575" }} />
							) : (
								<>
									{!disableActions && (
										<Dropdown menu={{ items }} trigger={["hover"]}>
											<a
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
												}}
											>
												<MoreOutlined />
											</a>
										</Dropdown>
									)}
								</>
							)}
						</Space>
					</div>
				</div>
			</div>
			<div className="card-content">
				<div className="text-secondary">
					<div className="card-content__title">{t("Chủ sở hữu", "Chủ sở hữu")}</div>
					<Flex gap={16} align="center">
						<Avatar src={record?.isMine ? owner?.avatar : owner?.universityMemberUrl} className="table-view-avatar">
							{getFirstLetter(record?.createdByName)}
						</Avatar>
						{record?.isMine ? (
							<div>{t("tôi", "tôi")}</div>
						) : (
							<div>
								<div className="font-weight-5">{owner?.universityMember}</div>
								<div>{owner?.name}</div>
							</div>
						)}
					</Flex>
				</div>
				{record?.updatedByName ? (
					<div className="text-secondary">
						<div className="card-content__title">{t("Chỉnh sửa lần cuối", "Chỉnh sửa lần cuối")}</div>
						{record?.isMine ? (
							<div>{dayjs(record?.updatedDate).format("HH:mm DD/MM/YYYY")}</div>
						) : (
							<Flex gap={16} align="center">
								<Avatar
									src={record?.enterpriseUpdateUrl}
									className="table-view-avatar"
								>
									{getFirstLetter(record?.enterpriseUpdateName)}
								</Avatar>
								<div>
									<div>
										<div className="font-weight-5">{record?.enterpriseUpdateName}</div>
										<div>{record?.updatedByName}</div>
										<div>{dayjs(record?.updatedDate).format("HH:mm DD/MM/YYYY")}</div>
									</div>
								</div>
							</Flex>
						)}
					</div>
				) : null}
			</div>
		</div>
	);
};

export default ResourceCard;